import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';

import type {
  CleanupApplyPlan,
  CleanupTypeVersion,
  DivergentGroup,
  GroupResolution,
  LooseEndDecision,
  LooseEndSet,
  WizardData,
} from '../types';
import { buildApplyPlan } from '../utils/applyPlan';
import { computeDiffFields, personalKey } from '../utils/deriveWizardData';
import {
  computeLooseEndSets,
  computeNearMatchSets,
  computeSharedCatalog,
  templateReuseCount,
} from '../utils/looseEnds';
import {
  commitSetDecision,
  computeQuickWinSets,
  defaultQuickWinDecision,
} from '../utils/quickWins';
import { computeRenameDigest } from '../utils/renameDigest';
import { soloStaleTypes } from '../utils/staleTypes';

import { ResolveDivergentGroup } from './ResolveDivergentGroup';
import { SetAsideRenames } from './SetAsideRenames';
import { Step0Snapshot } from './Step0Snapshot';
import { Step1ArchivedReview } from './Step1ArchivedReview';
import { Step3Confirm } from './Step3Confirm';
import { StepLooseEnds } from './StepLooseEnds';

export interface CleanupWizardProps {
  data: WizardData;
  /**
   * Fired on Apply with the normalized plan. May be async — the wizard awaits
   * it and shows a busy state on the Apply button while it runs.
   */
  onApply?: (plan: CleanupApplyPlan) => void | Promise<void>;
  /**
   * Fired on mount and whenever the active step changes — the container's
   * hook for progression analytics. Labels come from the adaptive step list
   * (fixed strings, safe as event metadata).
   */
  onStepChange?: (step: {
    label: string;
    index: number;
    total: number;
  }) => void;
}

const effective = (
  decisions: Readonly<Record<string, LooseEndDecision>>,
  set: LooseEndSet
): LooseEndDecision => decisions[set.key] ?? defaultQuickWinDecision(set);

/**
 * Consumption-side normalization: the raw decision keeps whatever the admin
 * typed (so they can clear the field mid-edit), but merges/digests never see
 * an empty name — it falls back to the junk-free suggestion.
 */
const committed = (
  decisions: Readonly<Record<string, LooseEndDecision>>,
  set: LooseEndSet
): LooseEndDecision => commitSetDecision(set, effective(decisions, set));

const toMerges = (
  sets: LooseEndSet[],
  decisions: Readonly<Record<string, LooseEndDecision>>
): Array<{ keepName: string; mergedNames: string[]; templateTitle: string | null }> =>
  sets
    .map((set) => ({ set, decision: committed(decisions, set) }))
    .filter(({ decision }) => decision.merge && !decision.archive)
    .map(({ set, decision }) => ({
      keepName: decision.keepName,
      mergedNames: set.entries
        .map((e) => e.name)
        .filter((n) => n !== decision.keepName),
      templateTitle: set.templateTitle,
    }));

const toArchives = (
  sets: LooseEndSet[],
  decisions: Readonly<Record<string, LooseEndDecision>>
): Array<{ names: string[]; templateTitle: string | null; count: number }> =>
  sets
    .map((set) => ({ set, decision: committed(decisions, set) }))
    .filter(({ decision }) => decision.archive)
    .map(({ set }) => ({
      names: set.entries.map((e) => e.name),
      templateTitle: set.templateTitle,
      count: set.entries.length,
    }));

interface FooterConfig {
  back?: { label: string; onClick: () => void };
  primary?: {
    label: string;
    onClick: () => void;
    color?: 'primary' | 'success';
    variant?: 'contained' | 'outlined';
    disabled?: boolean;
  };
  caption?: string;
}

/**
 * The practice cleanup wizard (GDN-2226) — hybrid model with the
 * confidence-first ordering the pilot data argued for: quick wins (identical
 * template clusters, zero judgment) come before the judgment lanes
 * (duplicates, personal copies), and the loose-ends sweep catches whatever
 * ends up interchangeable after merging. Renders as a full-height shell —
 * fixed header (title + stepper), one scrolling content region, sticky
 * footer that owns all navigation. Mock-first: this orchestrator holds the
 * flow state; the future container wraps it in a fullscreen Dialog and
 * wires onApply into the #3258 engine.
 */
export const CleanupWizard: React.FC<CleanupWizardProps> = ({
  data,
  onApply,
  onStepChange,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [groupIndex, setGroupIndex] = useState(0);
  const [clusterIndex, setClusterIndex] = useState(0);
  const [resolutions, setResolutions] = useState<
    Record<string, GroupResolution>
  >({});
  const [winnerByGroup, setWinnerByGroup] = useState<Record<string, string>>(
    {}
  );
  const [restoredIds, setRestoredIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [quickWinDecisions, setQuickWinDecisions] = useState<
    Record<string, LooseEndDecision>
  >({});
  const [looseEndDecisions, setLooseEndDecisions] = useState<
    Record<string, LooseEndDecision>
  >({});
  const [setAsideRenames, setSetAsideRenames] = useState<
    Record<string, string>
  >({});
  const [setAsideArchivedIds, setSetAsideArchivedIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [catalogArchivedIds, setCatalogArchivedIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [staleWeeks, setStaleWeeks] = useState(12);
  const [staleUncheckedIds, setStaleUncheckedIds] = useState<
    ReadonlySet<string>
  >(new Set());
  const [applying, setApplying] = useState(false);

  // One scroll context (the content region) — restore to top whenever the
  // step or the current group changes, so each screen starts at its title.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [activeStep, groupIndex, clusterIndex]);

  // ----- Quick wins: identical template clusters, decided up front --------
  const quickWinSets = useMemo(() => computeQuickWinSets(data), [data]);
  const quickWinResolved = quickWinSets.map((set) => ({
    set,
    decision: committed(quickWinDecisions, set),
  }));
  const quickWinAccepted = quickWinResolved.filter(
    ({ decision }) => decision.merge && !decision.archive
  );
  const quickWinArchivedSets = quickWinResolved.filter(
    ({ decision }) => decision.archive
  );

  // Accepted quick-win rows leave the judgment lanes entirely; survivors
  // (the kept name's most-used version) join the projected shared catalog.
  const { filteredData, sharedAdjustment } = useMemo(() => {
    const survivorIds = new Set<string>();
    const allIds = new Set<string>();
    // Survivors carry the merge RESULT into everything downstream: the
    // kept name and the absorbed usage of their merged siblings.
    const survivorOutcome = new Map<string, { name: string; usage: number }>();
    for (const { set, decision } of quickWinAccepted) {
      const survivor =
        set.entries.find((e) => e.name === decision.keepName)?.version ??
        set.entries[0].version;
      survivorIds.add(survivor.id);
      survivorOutcome.set(survivor.id, {
        name: decision.keepName,
        usage: set.entries.reduce((sum, e) => sum + e.version.usage, 0),
      });
      for (const e of set.entries) allIds.add(e.version.id);
    }
    // Archived sets: every row leaves the lanes AND the shared catalog.
    for (const { set } of quickWinArchivedSets) {
      for (const e of set.entries) allIds.add(e.version.id);
    }
    const sharedIds = new Set(data.sharedTypes.map((v) => v.id));
    const mergedAwayShared = [...allIds].filter(
      (id) => sharedIds.has(id) && !survivorIds.has(id)
    ).length;
    const survivorsNotShared = [...survivorIds].filter(
      (id) => !sharedIds.has(id)
    );

    const filterGroups = (groups: DivergentGroup[]): DivergentGroup[] =>
      groups
        .map((g) => {
          const versions = g.versions.filter((v) => !allIds.has(v.id));
          return {
            ...g,
            versions,
            displayName: versions[0]?.name ?? g.displayName,
            diffFields: computeDiffFields(versions),
          };
        })
        .filter((g) => g.versions.length >= 2);

    const byId = new Map(
      [...data.divergentGroups, ...data.personalClusters]
        .flatMap((g) => g.versions)
        .concat(data.sharedTypes, data.personalTypes)
        .map((v) => [v.id, v])
    );
    const enrich = (v: CleanupTypeVersion): CleanupTypeVersion => {
      const outcome = survivorOutcome.get(v.id);
      return outcome ? { ...v, name: outcome.name, usage: outcome.usage } : v;
    };
    const survivors = survivorsNotShared
      .map((id) => byId.get(id))
      .filter((v) => v !== undefined)
      .map(enrich);

    const filtered: WizardData = {
      ...data,
      sharedTypes: [
        ...data.sharedTypes
          .filter((v) => !allIds.has(v.id) || survivorIds.has(v.id))
          .map(enrich),
        ...survivors,
      ],
      divergentGroups: filterGroups(data.divergentGroups),
      personalClusters: filterGroups(data.personalClusters),
      personalTypes: data.personalTypes.filter((v) => !allIds.has(v.id)),
    };
    return {
      filteredData: filtered,
      sharedAdjustment: survivorsNotShared.length - mergedAwayShared,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, quickWinSets, quickWinDecisions]);

  const quickWinArchives = toArchives(quickWinSets, quickWinDecisions);

  // ----- Steps (labels adapt to what's left after quick wins) -------------
  const hasQuickWins = quickWinSets.length > 0;
  const hasPersonalStep = filteredData.personalClusters.length > 0;
  const stepLabels = [
    'Overview',
    'Archived',
    ...(hasQuickWins ? ['Quick wins'] : []),
    'Duplicates',
    ...(hasPersonalStep ? ['Personal copies'] : []),
    'Loose ends',
    'Confirm',
  ];
  const idx = (label: string): number => stepLabels.indexOf(label);

  useEffect(() => {
    onStepChange?.({
      label: stepLabels[activeStep] ?? '',
      index: activeStep,
      total: stepLabels.length,
    });
    // Deliberately not keyed on stepLabels: its identity changes every
    // render, but a label change without a step change isn't a transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, onStepChange]);

  const groups = filteredData.divergentGroups;
  const clusters = filteredData.personalClusters;
  const safeGroupIndex = Math.min(groupIndex, Math.max(groups.length - 1, 0));
  const safeClusterIndex = Math.min(
    clusterIndex,
    Math.max(clusters.length - 1, 0)
  );
  const currentGroup = groups[safeGroupIndex];
  const currentCluster = clusters[safeClusterIndex];
  const resolvedCount = groups.filter((g) => resolutions[g.nameKey]).length;
  const resolvedClusterCount = clusters.filter(
    (g) => resolutions[personalKey(g.nameKey)]
  ).length;

  // Stale-archive tool (Archived step): solo stale types — personal AND
  // about-to-be-auto-promoted — archive-by-default with per-row opt-out.
  const staleCandidates = useMemo(
    () => soloStaleTypes(filteredData, staleWeeks),
    [filteredData, staleWeeks]
  );
  const staleArchived = staleCandidates.filter(
    (t) => !staleUncheckedIds.has(t.id)
  );
  const staleArchivedIds = new Set(staleArchived.map((t) => t.id));
  // Everything downstream (loose ends, catalog, Confirm) sees stale-archived
  // rows out of BOTH the personal bucket and the shared catalog.
  const workingData: WizardData = useMemo(
    () => ({
      ...filteredData,
      sharedTypes: filteredData.sharedTypes.filter(
        (t) => !staleArchivedIds.has(t.id)
      ),
      personalTypes: filteredData.personalTypes.filter(
        (t) => !staleArchivedIds.has(t.id)
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredData, staleCandidates, staleUncheckedIds]
  );

  const looseEndSets = useMemo(
    () => computeLooseEndSets(workingData, resolutions),
    [workingData, resolutions]
  );
  // Tier 2: same template, compatible (union-able) defaults — opt-in.
  const nearMatchSets = useMemo(
    () =>
      computeNearMatchSets(
        workingData,
        resolutions,
        looseEndSets,
        Object.fromEntries(
          looseEndSets.map((s) => [s.key, committed(looseEndDecisions, s)])
        )
      ),
    [workingData, resolutions, looseEndSets, looseEndDecisions]
  );
  const allLooseSets = useMemo(
    () => [...looseEndSets, ...nearMatchSets],
    [looseEndSets, nearMatchSets]
  );
  const looseEndMerges = toMerges(allLooseSets, looseEndDecisions);
  const looseEndArchives = toArchives(allLooseSets, looseEndDecisions);
  const quickWinMerges = toMerges(quickWinSets, quickWinDecisions);
  const setArchives = [...quickWinArchives, ...looseEndArchives];

  // The resulting shared catalog — Confirm's picker preview / ground truth.
  // "Originally shared" = org-scoped BEFORE the wizard; auto-promoted rows
  // are newly shared by this run and must show the "new" chip.
  const originallySharedIds = useMemo(
    () =>
      new Set(
        data.sharedTypes
          .filter((v) => v.ownerScope === 'organization')
          .map((v) => v.id)
      ),
    [data]
  );

  const quickWinMergingCount = quickWinMerges.length;
  const looseEndMergingCount = looseEndMerges.length;

  // Template outliers set aside in the merge lanes — queued for renaming.
  // Lanes are keyed separately: personal-cluster resolutions live under
  // personalKey(nameKey) precisely so a same-named divergent group can't
  // bleed its resolution (and setAsideIds) into the cluster.
  const setAsideItems = useMemo(() => {
    const items: Array<{ version: CleanupTypeVersion; fromGroup: string }> = [];
    const collect = (
      groups: DivergentGroup[],
      keyOf: (nameKey: string) => string
    ): void => {
      for (const group of groups) {
        const resolution = resolutions[keyOf(group.nameKey)];
        if (resolution?.kind !== 'make_shared' || !resolution.setAsideIds) {
          continue;
        }
        for (const id of resolution.setAsideIds) {
          const version = group.versions.find((v) => v.id === id);
          if (version) items.push({ version, fromGroup: group.displayName });
        }
      }
    };
    collect(workingData.divergentGroups, (k) => k);
    collect(workingData.personalClusters, personalKey);
    return items;
  }, [workingData, resolutions]);
  // Raw values feed the input (so it can be cleared mid-edit); committed
  // values feed the digest — trimmed, falling back to the suggestion (the
  // template's name — what the type actually is), never empty.
  const suggestedSetAsideName = (version: CleanupTypeVersion): string =>
    version.templateTitle ?? version.name;
  const displaySetAsideRenames: Record<string, string> = Object.fromEntries(
    setAsideItems.map(({ version }) => [
      version.id,
      setAsideRenames[version.id] ?? suggestedSetAsideName(version),
    ])
  );
  const committedSetAsideRenames: Record<string, string> = Object.fromEntries(
    setAsideItems.map(({ version }) => [
      version.id,
      (setAsideRenames[version.id] ?? '').trim() ||
        suggestedSetAsideName(version),
    ])
  );

  // Built after the set-aside decisions so the picker preview reflects them:
  // archived set-asides drop out, renamed ones show their kept name (so a
  // collision the admin resolved stops flagging).
  const sharedCatalog = useMemo(
    () =>
      computeSharedCatalog({
        data: workingData,
        resolutions,
        looseEndSets: allLooseSets,
        looseEndDecisions: Object.fromEntries(
          allLooseSets.map((s) => [s.key, committed(looseEndDecisions, s)])
        ),
        originallySharedIds,
        setAsideArchivedIds,
        setAsideRenames: committedSetAsideRenames,
      }),
    [
      workingData,
      resolutions,
      allLooseSets,
      looseEndDecisions,
      originallySharedIds,
      setAsideArchivedIds,
      committedSetAsideRenames,
    ]
  );
  const renames = useMemo(
    () =>
      computeRenameDigest({
        data: workingData,
        resolutions,
        extraRenames: setAsideItems
          .filter(
            ({ version }) =>
              !setAsideArchivedIds.has(version.id) &&
              committedSetAsideRenames[version.id].trim() !==
                version.name.trim()
          )
          .map(({ version }) => ({
            provider: version.provider,
            oldName: version.name,
            newName: committedSetAsideRenames[version.id],
          })),
        quickWinSets,
        quickWinDecisions: Object.fromEntries(
          quickWinSets.map((s) => [s.key, committed(quickWinDecisions, s)])
        ),
        looseEndSets: allLooseSets,
        looseEndDecisions: Object.fromEntries(
          allLooseSets.map((s) => [s.key, committed(looseEndDecisions, s)])
        ),
      }),
    [
      workingData,
      resolutions,
      quickWinSets,
      quickWinDecisions,
      allLooseSets,
      looseEndDecisions,
      setAsideItems,
      committedSetAsideRenames,
      setAsideArchivedIds,
    ]
  );

  // Assemble the apply plan from the current decision state and hand it to the
  // container. Async: the container fans it out to the backend verbs; we hold
  // the Apply button busy until it settles.
  const handleApply = async (): Promise<void> => {
    if (applying) return;
    const plan = buildApplyPlan({
      divergentGroups: groups,
      personalClusters: clusters,
      resolutions,
      quickWinSets,
      looseEndSets: allLooseSets,
      quickWinDecisions: Object.fromEntries(
        quickWinSets.map((s) => [s.key, committed(quickWinDecisions, s)])
      ),
      looseEndDecisions: Object.fromEntries(
        allLooseSets.map((s) => [s.key, committed(looseEndDecisions, s)])
      ),
      restoreTypeIds: [...restoredIds],
      staleArchivedIds: [...staleArchivedIds],
      catalogArchivedIds: [...catalogArchivedIds],
      setAsideArchivedIds: [...setAsideArchivedIds],
      renames: setAsideItems
        .filter(
          ({ version }) =>
            !setAsideArchivedIds.has(version.id) &&
            committedSetAsideRenames[version.id].trim() !==
              version.name.trim()
        )
        .map(({ version }) => ({
          typeId: version.id,
          name: committedSetAsideRenames[version.id],
        })),
    });
    setApplying(true);
    try {
      await onApply?.(plan);
    } finally {
      setApplying(false);
    }
  };

  const resolveInLane = (
    lane: 'divergent' | 'personal',
    resolution: GroupResolution
  ): void => {
    const isPersonal = lane === 'personal';
    const list = isPersonal ? clusters : groups;
    const index = isPersonal ? safeClusterIndex : safeGroupIndex;
    const current = list[index];
    if (!current) return;
    const key = isPersonal ? personalKey(current.nameKey) : current.nameKey;
    setResolutions((prev) => ({ ...prev, [key]: resolution }));
    if (index < list.length - 1) {
      if (isPersonal) setClusterIndex(index + 1);
      else setGroupIndex(index + 1);
    } else {
      setActiveStep(
        isPersonal
          ? idx('Loose ends')
          : hasPersonalStep
            ? idx('Personal copies')
            : idx('Loose ends')
      );
    }
  };

  const renderLane = (
    lane: 'divergent' | 'personal',
    current: DivergentGroup,
    index: number,
    list: DivergentGroup[]
  ): React.ReactElement => {
    const key =
      lane === 'personal' ? personalKey(current.nameKey) : current.nameKey;
    const stored = resolutions[key];
    const storedWinnerId =
      stored?.kind === 'make_shared' &&
      current.versions.some((v) => v.id === stored.winnerId)
        ? stored.winnerId
        : null;
    const selectedId =
      winnerByGroup[key] &&
      current.versions.some((v) => v.id === winnerByGroup[key])
        ? winnerByGroup[key]
        : (storedWinnerId ?? current.versions[0]?.id ?? null);
    const currentWinner = current.versions.find((v) => v.id === selectedId);
    return (
      <ResolveDivergentGroup
        key={key}
        group={current}
        initialResolution={stored}
        variant={lane === 'personal' ? 'personalPair' : 'divergent'}
        templateReuseHint={
          lane === 'divergent' && currentWinner
            ? templateReuseCount(
                workingData,
                currentWinner.templateId,
                currentWinner.id
              )
            : 0
        }
        groupIndex={index}
        groupCount={list.length}
        selectedWinnerId={selectedId}
        onWinnerChange={(id) =>
          setWinnerByGroup((prev) => ({ ...prev, [key]: id }))
        }
        onMakeShared={({ winnerId, sharedName, adoptDefaultsFromId, setAsideIds }) =>
          resolveInLane(lane, {
            kind: 'make_shared',
            winnerId,
            sharedName,
            adoptDefaultsFromId,
            setAsideIds,
          })
        }
        onKeepSeparate={() => resolveInLane(lane, { kind: 'keep_separate' })}
        onArchiveAll={() => resolveInLane(lane, { kind: 'archive_all' })}
        onDecideLater={() => resolveInLane(lane, { kind: 'decide_later' })}
      />
    );
  };

  const laneBack = (lane: 'divergent' | 'personal'): void => {
    const index = lane === 'personal' ? safeClusterIndex : safeGroupIndex;
    if (index > 0) {
      if (lane === 'personal') setClusterIndex(index - 1);
      else setGroupIndex(index - 1);
    } else if (lane === 'personal') {
      setActiveStep(idx('Duplicates'));
    } else {
      setActiveStep(hasQuickWins ? idx('Quick wins') : idx('Archived'));
    }
  };

  // ----- Per-step content + footer ----------------------------------------
  let content: React.ReactNode = null;
  let footer: FooterConfig = {};

  if (activeStep === idx('Overview')) {
    content = (
      <Step0Snapshot snapshot={data.snapshot} quickWinSetCount={quickWinSets.length} />
    );
    footer = {
      primary: { label: 'Start cleanup →', onClick: () => setActiveStep(1) },
      caption: '~5 minutes · every change is reversible',
    };
  } else if (activeStep === idx('Archived')) {
    content = (
      <Step1ArchivedReview
        archived={data.archivedTypes}
        restoredIds={restoredIds}
        onToggleRestore={(id) =>
          setRestoredIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        staleCandidates={staleCandidates}
        autoPromoteBoundIds={
          new Set(
            filteredData.sharedTypes
              .filter((v) => v.ownerScope !== 'organization')
              .map((v) => v.id)
          )
        }
        staleWeeks={staleWeeks}
        onStaleWeeksChange={setStaleWeeks}
        staleUncheckedIds={staleUncheckedIds}
        onToggleStaleArchive={(id) =>
          setStaleUncheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
      />
    );
    footer = {
      back: { label: '← Back', onClick: () => setActiveStep(idx('Overview')) },
      primary: {
        label: 'Looks right, continue →',
        onClick: () =>
          setActiveStep(hasQuickWins ? idx('Quick wins') : idx('Duplicates')),
      },
      caption: [
        restoredIds.size > 0 ? `${restoredIds.size} restored` : null,
        staleArchived.length > 0
          ? `${staleArchived.length} stale types will archive`
          : null,
        'reversible anytime',
      ]
        .filter(Boolean)
        .join(' · '),
    };
  } else if (hasQuickWins && activeStep === idx('Quick wins')) {
    content = (
      <StepLooseEnds
        sets={quickWinSets}
        decisions={Object.fromEntries(
          quickWinSets.map((s) => [s.key, effective(quickWinDecisions, s)])
        )}
        onDecisionChange={(key, decision) =>
          setQuickWinDecisions((prev) => ({ ...prev, [key]: decision }))
        }
        heading="Quick wins — your providers already agree"
        lede={
          <>
            Each of these is a set of types with the <b>same template and
            identical defaults</b> — the only difference is the name.
            Consolidating them is safe and shrinks the work ahead; pick
            which name the practice keeps — or type a better one.
          </>
        }
      />
    );
    footer = {
      back: { label: '← Back', onClick: () => setActiveStep(idx('Archived')) },
      primary: {
        label: 'Continue →',
        onClick: () => setActiveStep(idx('Duplicates')),
      },
      caption: `${quickWinMergingCount} of ${quickWinSets.length} sets will merge${
        quickWinArchives.length > 0
          ? ` · ${quickWinArchives.length} will archive (stale)`
          : ''
      }`,
    };
  } else if (activeStep === idx('Duplicates')) {
    content = currentGroup ? (
      <>
        <Box sx={{ mb: 2, maxWidth: 720 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Duplicates — same name, built differently
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The wizard already handled the safe, identical merges. What&apos;s
            left is the judgment <b>only you can make</b> — same-named types
            that were actually built differently. Work through them one card at
            a time; nothing changes until you confirm.
          </Typography>
        </Box>
        {renderLane('divergent', currentGroup, safeGroupIndex, groups)}
      </>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Nothing left to review here — quick wins covered every duplicate
        group. 🎉
      </Typography>
    );
    // The per-group buttons (merge/keep/archive/decide later) are the real
    // "continue"; the footer only skips what's left — say so.
    const unresolvedGroups = groups.length - resolvedCount;
    footer = {
      back: { label: '← Back', onClick: () => laneBack('divergent') },
      primary: {
        label:
          currentGroup && unresolvedGroups > 0
            ? `Skip remaining ${unresolvedGroups} ${unresolvedGroups === 1 ? 'group' : 'groups'} →`
            : 'Continue →',
        variant: currentGroup && unresolvedGroups > 0 ? 'outlined' : 'contained',
        onClick: () =>
          setActiveStep(
            hasPersonalStep ? idx('Personal copies') : idx('Loose ends')
          ),
      },
      caption: currentGroup
        ? `Group ${safeGroupIndex + 1} of ${groups.length} · ${resolvedCount} resolved · nothing merges until you confirm`
        : undefined,
    };
  } else if (hasPersonalStep && activeStep === idx('Personal copies')) {
    content = currentCluster && (
      <>
        <Box sx={{ mb: 2, maxWidth: 720 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Personal copies — a different question
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Everything so far was about the shared catalog. These are
            individual providers&apos; own types, each wired to their own
            personal template. <b>Sharing here is opt-in</b> — keeping them
            personal is a perfectly good outcome. Promote a pair only when
            the whole practice should chart that way.
          </Typography>
        </Box>
        {renderLane('personal', currentCluster, safeClusterIndex, clusters)}
      </>
    );
    const unresolvedClusters = clusters.length - resolvedClusterCount;
    footer = {
      back: { label: '← Back', onClick: () => laneBack('personal') },
      primary: {
        label:
          unresolvedClusters > 0
            ? `Skip remaining ${unresolvedClusters} ${unresolvedClusters === 1 ? 'group' : 'groups'} →`
            : 'Continue →',
        variant: unresolvedClusters > 0 ? 'outlined' : 'contained',
        onClick: () => setActiveStep(idx('Loose ends')),
      },
      caption: `Group ${safeClusterIndex + 1} of ${clusters.length} · ${resolvedClusterCount} resolved · nothing merges until you confirm`,
    };
  } else if (activeStep === idx('Loose ends')) {
    content = (
      <>
        <StepLooseEnds
          sets={allLooseSets}
          decisions={Object.fromEntries(
            allLooseSets.map((s) => [s.key, effective(looseEndDecisions, s)])
          )}
          onDecisionChange={(key, decision) =>
            setLooseEndDecisions((prev) => ({ ...prev, [key]: decision }))
          }
        />
        <SetAsideRenames
          items={setAsideItems}
          renames={displaySetAsideRenames}
          onRenameChange={(versionId, name) =>
            setSetAsideRenames((prev) => ({ ...prev, [versionId]: name }))
          }
          archivedIds={setAsideArchivedIds}
          onToggleArchive={(versionId) =>
            setSetAsideArchivedIds((prev) => {
              const next = new Set(prev);
              if (next.has(versionId)) next.delete(versionId);
              else next.add(versionId);
              return next;
            })
          }
        />
      </>
    );
    footer = {
      back: {
        label: '← Back',
        onClick: () =>
          setActiveStep(
            hasPersonalStep ? idx('Personal copies') : idx('Duplicates')
          ),
      },
      primary: {
        label: 'Continue →',
        onClick: () => setActiveStep(idx('Confirm')),
      },
      caption:
        allLooseSets.length > 0
          ? `${looseEndMergingCount} of ${allLooseSets.length} sets will merge${
              nearMatchSets.length > 0
                ? ` · ${nearMatchSets.length} near ${nearMatchSets.length === 1 ? 'match' : 'matches'} to consider`
                : ''
            }`
          : undefined,
    };
  } else if (activeStep === idx('Confirm')) {
    content = (
      <Step3Confirm
        data={workingData}
        resolutions={resolutions}
        restoredCount={restoredIds.size}
        staleArchived={{ count: staleArchived.length, weeks: staleWeeks }}
        extraArchivedCount={setAsideArchivedIds.size}
        setArchives={setArchives}
        catalog={sharedCatalog}
        catalogArchivedIds={catalogArchivedIds}
        onToggleCatalogArchive={(id) =>
          setCatalogArchivedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        sharedAdjustment={sharedAdjustment}
        quickWinMerges={quickWinMerges}
        looseEndMerges={looseEndMerges}
        renames={renames}
        applying={applying}
        showApply={false}
        onApply={() => {
          void handleApply();
        }}
      />
    );
    footer = {
      back: {
        label: '← Back to loose ends',
        onClick: () => setActiveStep(idx('Loose ends')),
      },
      primary: {
        label: 'Apply these changes',
        color: 'success',
        disabled: applying,
        onClick: () => {
          void handleApply();
        },
      },
      caption: 'Reversible — every change is provenance-logged',
    };
  }

  return (
    <Box
      sx={(theme) => ({
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.default,
      })}
    >
      <Box
        sx={(theme) => ({
          px: 3,
          pt: 2,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        })}
      >
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Clean up appointment types
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data.snapshot.orgLabel}
            </Typography>
          </Box>
          <Stepper activeStep={activeStep} sx={{ mt: 1.5 }}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Box>

      <Box ref={scrollRef} sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>{content}</Box>
      </Box>

      <Box
        sx={(theme) => ({
          px: 3,
          py: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          flexShrink: 0,
        })}
      >
        <Box
          sx={{
            maxWidth: 1100,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {footer.back && (
            <Button color="inherit" onClick={footer.back.onClick}>
              {footer.back.label}
            </Button>
          )}
          {footer.primary && (
            <Button
              variant={footer.primary.variant ?? 'contained'}
              color={footer.primary.color ?? 'primary'}
              onClick={footer.primary.onClick}
              disabled={footer.primary.disabled}
            >
              {footer.primary.label}
            </Button>
          )}
          {footer.caption && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 'auto', textAlign: 'right' }}
            >
              {footer.caption}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
